import { useMemo, useState, useRef, useEffect } from 'react';
import { FileText, Video } from 'lucide-react';

const LOCAL_VIDEO_URL = '/video/Fundacja-Znajdka-PJM.mp4';

const TRANSCRIPT_ENTRIES = [
  { id: '1', start: '00:00:02,859', end: '00:00:09,880', text: ['Fundacja Znajdki powstała w 2012 roku,'] },
  {
    id: '2',
    start: '00:00:09,880',
    end: '00:00:17,039',
    text: ['aby poprawić los bezdomnych zwierząt', 'i dać im szansę na nowe, lepsze życie.'],
  },
  {
    id: '3',
    start: '00:00:17,039',
    end: '00:00:23,920',
    text: ['Każdego roku ratujemy', 'około 300 zwierząt- psów i kotów,'],
  },
  { id: '4', start: '00:00:23,920', end: '00:00:29,019', text: ['które zostały porzucone', 'lub odebrane ze złych warunków.'] },
  { id: '5', start: '00:00:29,019', end: '00:00:34,979', text: ['Pomagamy kotom', 'wolnożyjącym stawiając dla nich budki'] },
  { id: '6', start: '00:00:34,979', end: '00:00:38,200', text: ['i dokarmiając i dbając o ich zdrowie.'] },
  {
    id: '7',
    start: '00:00:38,200',
    end: '00:00:45,420',
    text: ['Każde zwierzę, które do nas trafia', 'jest leczone, zaszczepione i kastrowane,'],
  },
  { id: '8', start: '00:00:45,420', end: '00:00:49,460', text: ['aby nie szerzyć bezdomności.'] },
  { id: '9', start: '00:00:49,460', end: '00:00:53,979', text: ['Staramy się, aby wszystkie', 'zwierzęta znalazły dobre domy,'] },
  { id: '10', start: '00:00:53,979', end: '00:00:59,600', text: ['dlatego ważna jest dla nas', 'odpowiedzialna adopcja.'] },
  {
    id: '11',
    start: '00:00:59,600',
    end: '00:01:05,060',
    text: ['Zwierzęta bezdomne', 'są w złym stanie - chore, zagłodzone,'],
  },
  {
    id: '12',
    start: '00:01:05,060',
    end: '00:01:09,620',
    text: ['bojące się dotyku człowieka,', 'potrzebują troski i miłości.'],
  },
  { id: '13', start: '00:01:09,620', end: '00:01:15,260', text: ['Dzięki darczyńcom', 'możemy zmieniać ich los.'] },
  {
    id: '14',
    start: '00:01:15,260',
    end: '00:01:20,579',
    text: ['Zapraszamy do  przytuliska,', 'aby poznać nas i naszych podopiecznych.'],
  },
  {
    id: '15',
    start: '00:01:20,579',
    end: '00:01:31,140',
    text: ['Wolontariat, adopcja wirtualna, zbiórki darów', 'i wsparcie finansowe, to dla nas ważna pomoc.'],
  },
  {
    id: '16',
    start: '00:01:31,140',
    end: '00:01:39,680',
    text: ['O naszych działaniach i potrzebach możecie', 'przeczytać na stronie www.znajdki.pl i znajdki fb.'],
  },
  { id: '17', start: '00:01:39,680', end: '00:01:42,439', text: ['Adres przytuliska: Krusze ul. Kryształowa 171'] },
  { id: '18', start: '00:01:45,079', end: '00:01:46,159', text: ['telefon 664-821-160'] },
];

// Convert SRT time format (00:00:02,859) to seconds
function timeToSeconds(time: string): number {
  const [hours, minutes, secondsWithMs] = time.split(':');
  const [seconds, milliseconds] = secondsWithMs.split(',');
  return (
    parseInt(hours) * 3600 +
    parseInt(minutes) * 60 +
    parseInt(seconds) +
    parseInt(milliseconds) / 1000
  );
}

export function SignLanguageVideo() {
  const [highlightedId, setHighlightedId] = useState<string>(TRANSCRIPT_ENTRIES[0]?.id ?? '');
  const videoRef = useRef<HTMLVideoElement>(null);

  const highlightedEntry = useMemo(
    () => TRANSCRIPT_ENTRIES.find((entry) => entry.id === highlightedId) ?? TRANSCRIPT_ENTRIES[0],
    [highlightedId]
  );

  // Update highlighted subtitle based on current video time
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const currentTime = video.currentTime;
      
      // Find the subtitle entry that matches current time
      const currentEntry = TRANSCRIPT_ENTRIES.find((entry) => {
        const startTime = timeToSeconds(entry.start);
        const endTime = timeToSeconds(entry.end);
        return currentTime >= startTime && currentTime <= endTime;
      });

      if (currentEntry && currentEntry.id !== highlightedId) {
        setHighlightedId(currentEntry.id);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [highlightedId]);

  // Handle clicking on a transcript entry to seek video
  const handleTranscriptClick = (entryId: string) => {
    const entry = TRANSCRIPT_ENTRIES.find((e) => e.id === entryId);
    if (entry && videoRef.current) {
      const startTime = timeToSeconds(entry.start);
      videoRef.current.currentTime = startTime;
      setHighlightedId(entryId);
    }
  };

  return (
    <section className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
          <Video className="w-6 h-6 text-accessible-red-600" aria-hidden="true" />
        </div>
        <div>
          <h2 className="card-title">Film w Polskim Języku Migowym</h2>
          <p className="body-medium text-accessible-gray-700 dark:text-accessible-gray-300">
            Film prezentuje działalność Fundacji Znajdki i sposoby, w jakie można wesprzeć nasze działania.
          </p>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start">
        <div className="space-y-4">
          <div className="rounded-xl overflow-hidden border border-accessible-gray-200 dark:border-accessible-gray-700 bg-black">
            <video
              ref={videoRef}
              className="w-full aspect-video"
              controls
              autoPlay
              muted
              playsInline
              preload="auto"
            >
              <source src={LOCAL_VIDEO_URL} type="video/mp4" />
              <track
                kind="captions"
                src="/docs/PJM/fundacja-znajdki.srt"
                srcLang="pl"
                label="Polski"
                default
              />
              Twoja przeglądarka nie obsługuje odtwarzacza wideo HTML5.
            </video>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-accessible-red-200 dark:border-accessible-red-900/60 bg-accessible-red-50/80 dark:bg-accessible-red-900/15 high-contrast:bg-white high-contrast:border-black high-contrast:shadow-none shadow-inner">
            <div className="px-4 py-3 flex flex-col gap-3">
              <span className="body-medium font-semibold text-accessible-red-800 dark:text-accessible-red-200 high-contrast:text-black">
                Aktualne napisy (PJM)
              </span>

              {highlightedEntry && (
                <div className="rounded-md bg-white dark:bg-accessible-gray-900 high-contrast:bg-white high-contrast:border-black px-4 py-3 border border-accessible-red-200 dark:border-accessible-red-800">
                  <p className="body-medium text-accessible-gray-900 dark:text-accessible-gray-100 high-contrast:!text-black">
                    {highlightedEntry.start.replace(',', '.')} → {highlightedEntry.end.replace(',', '.')}
                  </p>
                  <div className="body-medium text-accessible-gray-800 dark:text-accessible-gray-200 high-contrast:!text-black mt-1 space-y-2">
                    {highlightedEntry.text.map((line, index) => (
                      <p key={index} className="font-bold high-contrast:!text-black">
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <details className="group border-t border-accessible-gray-200 dark:border-accessible-gray-700 pt-3 px-4 pb-4 bg-white/80 dark:bg-accessible-gray-900/40 high-contrast:bg-white">
              <summary className="flex items-center justify-between gap-2 px-4 py-3 cursor-pointer body-small font-semibold text-accessible-gray-900 dark:text-accessible-gray-100 high-contrast:!text-black">
                Zobacz pełną transkrypcję ({TRANSCRIPT_ENTRIES.length})
                <svg
                  className="w-4 h-4 transition-transform duration-200 group-open:rotate-180"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </summary>
              <div
                className="pb-4 space-y-3 max-h-72 overflow-y-auto high-contrast:bg-white high-contrast:text-black"
                role="list"
                aria-label="Pełna lista napisów do filmu"
              >
                {TRANSCRIPT_ENTRIES.map((entry) => (
                  <div
                    key={entry.id}
                    role="listitem"
                    className={`rounded-md px-4 py-3 border transition-colors transcript-entry cursor-pointer ${
                      entry.id === highlightedId
                        ? 'bg-accessible-red-100/90 dark:bg-accessible-red-900/40 high-contrast:bg-white border-accessible-red-400 dark:border-accessible-red-700 high-contrast:border-black shadow-md'
                        : 'bg-white/80 dark:bg-accessible-gray-900/60 high-contrast:bg-white border-accessible-gray-200 dark:border-accessible-gray-700 high-contrast:border-black'
                    }`}
                    onClick={() => handleTranscriptClick(entry.id)}
                  >
                    <p className="body-small text-accessible-gray-900 dark:text-accessible-gray-100 high-contrast:!text-black">
                      {entry.start.replace(',', '.')} → {entry.end.replace(',', '.')}
                    </p>
                    <div className="body-small text-accessible-gray-800 dark:text-accessible-gray-200 high-contrast:!text-black mt-1 space-y-1 transcript-entry-text">
                      {entry.text.map((line, index) => (
                        <p key={index} className="font-bold high-contrast:!text-black">
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </details>

            <a
              href="/docs/PJM/fundacja-znajdki.srt"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-accessible-red-400 dark:border-accessible-red-700 high-contrast:border-black bg-accessible-red-100/80 dark:bg-accessible-red-900/30 high-contrast:bg-white px-3 py-2 body-small font-semibold text-accessible-red-800 dark:text-accessible-red-200 high-contrast:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-accessible-red-500"
              download
            >
              <FileText className="w-4 h-4" aria-hidden="true" /> Pobierz plik `.srt`
            </a>
          </div>
        </aside>
      </div>
    </section>
  );
}


